import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Layers, KeyRound, Loader2, RefreshCw, Unplug, Plus, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  usePancakeConfig,
  useSavePancakeToken,
  usePancakePages,
  usePancakeConnected,
  useConnectPancakePage,
  useDisconnectPancakePage,
  useSyncPancakePage,
  statusMeta,
  type PancakePage,
} from '@/hooks/use-integrations'
import { ChannelCard } from './channel-card'
import { noConnectHint } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'
import { useQuotaExceededDialog } from '@/components/billing/quota-exceeded-dialog'

function TokenForm({ hasToken }: { hasToken: boolean }) {
  const { t } = useTranslation('chanSocial')
  const [token, setToken] = useState('')
  const save = useSavePancakeToken()

  const handleSave = () => {
    if (!token.trim()) {
      toast.error(t('pancake.token.required'))
      return
    }
    save.mutate(token.trim(), {
      onSuccess: () => {
        toast.success(t('pancake.token.saved'))
        setToken('')
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" />
          {hasToken ? t('pancake.token.titleUpdate') : t('pancake.token.titleSetup')}
          {hasToken && (
            <Badge variant="success" className="ml-1">
              {t('shared.configured')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('pancake.token.intro')}
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Pancake User Access Token"
            className="min-w-[240px] flex-1"
          />
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />}
            {t('pancake.token.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function AvailablePages() {
  const { t } = useTranslation('chanSocial')
  const { data, isLoading, isError } = usePancakePages(true)
  const connect = useConnectPancakePage()
  // Kết nối trang = POST /pancake/connect → cần `integrations.create`.
  const canCreate = !!useMyPermissions().data?.has('integrations.create')
  const quotaDialog = useQuotaExceededDialog()

  const handleConnect = (p: PancakePage) => {
    connect.mutate(
      { pageId: p.id, pageName: p.name, platform: p.platform },
      {
        onSuccess: () => toast.success(t('pancake.available.connected', { name: p.name })),
        onError: (e) => { if (!quotaDialog.handle(e)) toast.error(apiError(e)) },
      },
    )
  }

  if (isLoading) return <Loading label={t('pancake.available.loading')} />
  if (isError) return <ErrorState message={t('pancake.available.error')} />
  if (!data?.length)
    return (
      <EmptyState
        icon={Layers}
        title={t('pancake.available.emptyTitle')}
        description={t('pancake.available.emptyDescription')}
      />
    )

  return (
    <div className="space-y-3">
      {quotaDialog.node}
      {data.map((p) => (
        <ChannelCard
          key={p.id}
          icon={<Layers className="h-5 w-5" />}
          title={p.name}
          subtitle={
            <>
              {p.platformLabel}
              {p.activeUsers ? ` · ${t('pancake.available.users', { count: p.activeUsers })}` : ''}
            </>
          }
          status={
            p.isConnected
              ? { label: t('common:status.connected'), variant: 'success' }
              : { label: t('common:status.notConnected'), variant: 'secondary' }
          }
          actions={
            p.isConnected ? (
              <span className="flex items-center gap-1 text-xs text-success">
                <CheckCircle2 className="h-4 w-4" />
              </span>
            ) : canCreate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleConnect(p)}
                disabled={connect.isPending || !p.hasToken}
                title={p.hasToken ? t('pancake.available.connectPage') : t('pancake.available.noPageToken')}
              >
                <Plus /> {t('common:actions.connect')}
              </Button>
            )
          }
        />
      ))}
    </div>
  )
}

function ConnectedPancakeAccounts() {
  const { t } = useTranslation('chanSocial')
  const { data, isLoading, isError } = usePancakeConnected()
  const sync = useSyncPancakePage()
  const remove = useDisconnectPancakePage()
  // Đồng bộ = POST /pancake/sync (update), ngắt = DELETE /pancake/disconnect (delete).
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canUpdate = !!perms?.has('integrations.update')
  const canDelete = !!perms?.has('integrations.delete')

  const handleSync = (id: string) => {
    sync.mutate(id, {
      onSuccess: (r) => toast.success(r.message || t('pancake.connected.syncStarted')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handleDisconnect = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success(t('pancake.connected.disconnected')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  if (isLoading) return <Loading label={t('pancake.connected.loading')} />
  if (isError) return <ErrorState />
  if (!data?.length)
    return (
      <EmptyState
        icon={Layers}
        title={t('pancake.connected.emptyTitle')}
        description={canCreate ? t('pancake.connected.emptyDescription') : noConnectHint()}
      />
    )

  return (
    <div className="space-y-3">
      {data.map((acc) => (
        <ChannelCard
          key={acc.id}
          icon={<Layers className="h-5 w-5" />}
          title={acc.displayName || 'Pancake Page'}
          subtitle={
            <>
              {acc.platformLabel}
              {acc.lastConnectedAt
                ? ` · ${t('pancake.connected.lastConnected', { time: dayjs(acc.lastConnectedAt).format('DD/MM/YYYY HH:mm') })}`
                : ''}
            </>
          }
          status={statusMeta(acc.status)}
          actions={
            <>
              {canUpdate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSync(acc.id)}
                  disabled={sync.isPending}
                >
                  <RefreshCw /> {t('pancake.connected.sync')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnect(acc.id)}
                  disabled={remove.isPending}
                  title={t('common:actions.disconnect')}
                >
                  <Unplug />
                </Button>
              )}
            </>
          }
        />
      ))}
    </div>
  )
}

export function PancakeTab() {
  const { t } = useTranslation('chanSocial')
  const { data: config, isLoading, isError } = usePancakeConfig()
  // Lưu token = POST /pancake/config → cần `integrations.update`; thiếu quyền thì ẩn cả ô nhập.
  const canUpdate = !!useMyPermissions().data?.has('integrations.update')

  if (isLoading) return <Loading label={t('pancake.loading')} />
  if (isError) return <ErrorState />

  const hasToken = !!config?.hasToken

  return (
    <div className="space-y-6">
      {canUpdate && <TokenForm hasToken={hasToken} />}

      {hasToken ? (
        <>
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('pancake.connected.heading')}</h3>
            <ConnectedPancakeAccounts />
          </section>
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('pancake.available.heading')}</h3>
            <AvailablePages />
          </section>
        </>
      ) : (
        <EmptyState
          icon={KeyRound}
          title={t('pancake.emptyTitle')}
          description={
            canUpdate ? t('pancake.emptyDescription') : noConnectHint()
          }
        />
      )}
    </div>
  )
}
